// PTZ Controller — AW-RP50-style, Tier 2 vector fidelity, 3-cam mode-aware.
//
// Three CAM-select buttons (CAM 1 · CAM 2 · CAM 3) match the
// CO-160a small-tile roster. All three are PTZ — the labels are
// symmetric; we don't single CAM 2 out with a "PTZ" suffix.
// Mode-aware: in `ic_manila` all three are
// enabled (one operator drives the whole rig); in `standard_assembly`
// only the seat's assigned cam is enabled (per-seat dedicated PTZ
// controllers).
//
// Doctrine: ADR-0015 §10 (interactive single-user simulators).
// Fidelity contract: docs/insights/tier-2-vector-fidelity.md.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HelpShell } from "./help-shell";
import { ScaledSurface } from "./scaled-surface";
import MultiviewCO160a from "./multiview-co-160a";
import type { MultiviewBusSource, MultiviewSourceId } from "./multiview-layout";
import {
  defaultCameraFramingFor,
  type CameraFraming,
  type ControllableCam,
} from "./scenario-camera-state";
import { useScenario } from "./scene/ScenarioContext";
import {
  clampCrop,
  lerpCrop,
  nudgeCrop,
  zoomCrop,
  type CropRect,
  type Preset,
  type Scenario,
  type ScenarioId,
} from "./scene/types";

const CAMS: { id: ControllableCam; label: string; tile: MultiviewSourceId }[] = [
  { id: "cam1", label: "CAM 1", tile: "cam1" },
  { id: "cam2", label: "CAM 2", tile: "cam2" },
  { id: "cam3", label: "CAM 3", tile: "cam3" },
];

// Physical AW-RP50 preset pad order: 1, 2, …, 9, 0. Button 0 sits at the
// end of the pad; presets[9] in the scenario's per-cam list maps to it.
const BUTTON_ORDER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0] as const;

function presetForButton(
  scenario: Scenario,
  cam: ControllableCam,
  button: number,
): Preset | undefined {
  const list = scenario.presets[cam] ?? [];
  const index = button === 0 ? 9 : button - 1;
  return list[index];
}

// Override storage — keyed by scenario × cam × preset id so a stored framing
// for "interview-segment / cam2 / speaker-mcu" doesn't bleed into prayer or
// baptism. Versioned prefix lets us migrate the shape later without
// silently corrupting an operator's stored framings.
type AllOverrides = Partial<
  Record<ScenarioId, Partial<Record<ControllableCam, Record<string, CropRect>>>>
>;

const STORAGE_KEY = "jw-av:rp50-overrides:v1";

function loadAllOverrides(): AllOverrides {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as AllOverrides;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function persistAllOverrides(next: AllOverrides) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage may be unavailable (Safari private mode, quota); the
    // in-memory state still works for the current session.
  }
}

export type PTZSeatState = {
  active_cam: ControllableCam;
  active_preset: number | null;
  pos: CropRect;
};

export type PTZAction =
  | { kind: "select_cam"; target: ControllableCam; ts_ms: number }
  | { kind: "recall_preset"; cam: ControllableCam; preset: number; ts_ms: number }
  | { kind: "store_preset"; cam: ControllableCam; preset: number; ts_ms: number }
  | { kind: "joystick"; cam: ControllableCam; dx: number; dy: number; ts_ms: number }
  | { kind: "zoom"; cam: ControllableCam; delta: number; ts_ms: number };

export type PTZCameraMode = "ic_manila" | "standard_assembly";

export interface PTZSimulatorProps {
  /** Controls which CAM-select buttons are enabled. */
  cameraMode?: PTZCameraMode;
  /**
   * In `standard_assembly` mode, the seat's assigned camera is the only
   * one this controller can drive. Defaults to `cam1` for solo practice.
   */
  assignedCam?: ControllableCam;
  /**
   * Controlled program source for the multiview. When omitted, defaults
   * to cam2 so the panel works standalone.
   */
  pgmSource?: MultiviewBusSource;
  /**
   * Controlled preview source for the multiview. When omitted, defaults
   * to the active camera tile so the panel works standalone.
   */
  pvwSource?: MultiviewBusSource;
  /**
   * EquipmentSimulator wrapper passes config as a single `initialState`
   * blob. Anything in here overrides the top-level props above (which
   * remain supported for direct mounts in the sandbox / drills).
   */
  initialState?: {
    cameraMode?: PTZCameraMode;
    assignedCam?: ControllableCam;
    pgmSource?: MultiviewBusSource;
    pvwSource?: MultiviewBusSource;
  };
  onStateChange?: (state: PTZSeatState) => void;
  onAction?: (action: PTZAction) => void;
}

const JOYSTICK_MAX = 40;
const RECALL_DURATION_MS = 300;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return reduced;
}

export default function PTZSimulator({
  cameraMode: cameraModeProp,
  assignedCam: assignedCamProp,
  pgmSource: pgmSourceProp,
  pvwSource: pvwSourceProp,
  initialState,
  onStateChange,
  onAction,
}: PTZSimulatorProps = {}) {
  const cameraMode: PTZCameraMode =
    initialState?.cameraMode ?? cameraModeProp ?? "ic_manila";
  const assignedCam: ControllableCam =
    initialState?.assignedCam ?? assignedCamProp ?? "cam1";
  const controlledPgm: MultiviewBusSource | undefined =
    initialState?.pgmSource ?? pgmSourceProp;
  const controlledPvw: MultiviewBusSource | undefined =
    initialState?.pvwSource ?? pvwSourceProp;
  const { scenario } = useScenario();
  const [activeCam, setActiveCam] = useState<ControllableCam>(assignedCam);
  const [activePreset, setActivePreset] = useState<number | null>(null);
  const [cameraFraming, setCameraFraming] = useState<CameraFraming>(() =>
    defaultCameraFramingFor(scenario),
  );
  // Per-scenario × per-cam overrides written by STORE; keyed by preset id
  // (string). Recall checks overrides first and falls back to the
  // scenario's factory crop. Persisted to localStorage so an operator's
  // tweaks survive a page reload.
  const [allOverrides, setAllOverrides] = useState<AllOverrides>(loadAllOverrides);
  const scenarioOverrides = allOverrides[scenario.id] ?? {};
  const [joystickActive, setJoystickActive] = useState(false);
  const [joystickPos, setJoystickPos] = useState({ x: 0, y: 0 });
  const [speed, setSpeed] = useState<1 | 2 | 3 | 4>(2);
  const joyRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number | undefined>(undefined);
  const prefersReducedMotion = usePrefersReducedMotion();

  const activeCrop = cameraFraming[activeCam];

  useEffect(() => {
    onStateChange?.({
      active_cam: activeCam,
      active_preset: activePreset,
      pos: activeCrop,
    });
  }, [activeCam, activePreset, activeCrop, onStateChange]);

  useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  // Scenario swap (e.g. operator picks "prayer" from the scenario chip-row)
  // resets the live PTZ framing to the new scenario's defaults and clears
  // the recalled-preset highlight. Stored overrides aren't wiped — they
  // live in `allOverrides` keyed by scenario id, so the operator's tweaks
  // for each scenario persist independently.
  useEffect(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    setCameraFraming(defaultCameraFramingFor(scenario));
    setActivePreset(null);
  }, [scenario]);

  const updateActiveCrop = useCallback(
    (updater: (crop: CropRect) => CropRect) => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      setCameraFraming((prev) => ({
        ...prev,
        [activeCam]: clampCrop(updater(prev[activeCam])),
      }));
    },
    [activeCam],
  );

  const animateActiveCropTo = useCallback(
    (nextCrop: CropRect) => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (prefersReducedMotion) {
        setCameraFraming((prev) => ({ ...prev, [activeCam]: clampCrop(nextCrop) }));
        return;
      }
      const startedAt = performance.now();
      const fromCrop = cameraFraming[activeCam];
      const toCrop = clampCrop(nextCrop);
      const step = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / RECALL_DURATION_MS);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCameraFraming((prev) => ({
          ...prev,
          [activeCam]: lerpCrop(fromCrop, toCrop, eased),
        }));
        if (progress < 1) {
          animRef.current = requestAnimationFrame(step);
        }
      };
      animRef.current = requestAnimationFrame(step);
    },
    [activeCam, cameraFraming, prefersReducedMotion],
  );

  const isCamEnabled = useCallback(
    (id: ControllableCam) => {
      if (cameraMode === "ic_manila") return true;
      return id === assignedCam;
    },
    [cameraMode, assignedCam],
  );

  const selectCam = useCallback(
    (id: ControllableCam) => {
      if (!isCamEnabled(id)) return;
      setActiveCam(id);
      onAction?.({ kind: "select_cam", target: id, ts_ms: Date.now() });
    },
    [isCamEnabled, onAction],
  );

  const recallPreset = useCallback(
    (button: number) => {
      const preset = presetForButton(scenario, activeCam, button);
      const override = preset
        ? scenarioOverrides[activeCam]?.[preset.id]
        : undefined;
      if (!preset && !override) return;
      const pos = override ?? preset!.crop;
      setActivePreset(button);
      animateActiveCropTo(pos);
      onAction?.({
        kind: "recall_preset",
        cam: activeCam,
        preset: button,
        ts_ms: Date.now(),
      });
    },
    [activeCam, animateActiveCropTo, onAction, scenario, scenarioOverrides],
  );

  const storePreset = useCallback(
    (button: number) => {
      const preset = presetForButton(scenario, activeCam, button);
      if (!preset) return;
      const next: AllOverrides = {
        ...allOverrides,
        [scenario.id]: {
          ...allOverrides[scenario.id],
          [activeCam]: {
            ...allOverrides[scenario.id]?.[activeCam],
            [preset.id]: { ...activeCrop },
          },
        },
      };
      setAllOverrides(next);
      persistAllOverrides(next);
      onAction?.({
        kind: "store_preset",
        cam: activeCam,
        preset: button,
        ts_ms: Date.now(),
      });
    },
    [activeCam, activeCrop, allOverrides, onAction, scenario],
  );

  // Keyboard controls.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      const step = 6 * speed;
      if (e.key === "ArrowUp") {
        e.preventDefault();
        updateActiveCrop((prev) => nudgeCrop(prev, 0, -(step / 5000)));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        updateActiveCrop((prev) => nudgeCrop(prev, 0, step / 5000));
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        updateActiveCrop((prev) => nudgeCrop(prev, -(step / 5000), 0));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        updateActiveCrop((prev) => nudgeCrop(prev, step / 5000, 0));
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        updateActiveCrop((prev) => zoomCrop(prev, 0.9));
        onAction?.({ kind: "zoom", cam: activeCam, delta: +0.1, ts_ms: Date.now() });
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        updateActiveCrop((prev) => zoomCrop(prev, 1.1));
        onAction?.({ kind: "zoom", cam: activeCam, delta: -0.1, ts_ms: Date.now() });
      } else if (/^\d$/.test(e.key)) {
        recallPreset(Number(e.key));
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeCam, speed, recallPreset, onAction, updateActiveCrop]);

  // Joystick drag handlers.
  const handleJoyStart = useCallback(
    (clientX: number, clientY: number) => {
      if (!joyRef.current) return;
      setJoystickActive(true);
      const rect = joyRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = Math.max(-JOYSTICK_MAX, Math.min(JOYSTICK_MAX, clientX - cx));
      const dy = Math.max(-JOYSTICK_MAX, Math.min(JOYSTICK_MAX, clientY - cy));
      setJoystickPos({ x: dx, y: dy });
      const speedFactor = (speed / 4) * 1.6;
      updateActiveCrop((prev) =>
        nudgeCrop(
          prev,
          (dx / JOYSTICK_MAX) * speedFactor * 0.006,
          (dy / JOYSTICK_MAX) * speedFactor * 0.006,
        ),
      );
      onAction?.({ kind: "joystick", cam: activeCam, dx, dy, ts_ms: Date.now() });
    },
    [activeCam, speed, onAction, updateActiveCrop],
  );

  const handleJoyMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!joystickActive || !joyRef.current) return;
      const rect = joyRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = Math.max(-JOYSTICK_MAX, Math.min(JOYSTICK_MAX, clientX - cx));
      const dy = Math.max(-JOYSTICK_MAX, Math.min(JOYSTICK_MAX, clientY - cy));
      setJoystickPos({ x: dx, y: dy });
      const speedFactor = (speed / 4) * 1.6;
      updateActiveCrop((prev) =>
        nudgeCrop(
          prev,
          (dx / JOYSTICK_MAX) * speedFactor * 0.006,
          (dy / JOYSTICK_MAX) * speedFactor * 0.006,
        ),
      );
    },
    [joystickActive, speed, updateActiveCrop],
  );

  const handleJoyEnd = useCallback(() => {
    setJoystickActive(false);
    setJoystickPos({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (!joystickActive) return;
    const move = (e: MouseEvent) => handleJoyMove(e.clientX, e.clientY);
    const up = () => handleJoyEnd();
    const tmove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) handleJoyMove(t.clientX, t.clientY);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", tmove);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", tmove);
      window.removeEventListener("touchend", up);
    };
  }, [joystickActive, handleJoyMove, handleJoyEnd]);

  const activeCamLabel = CAMS.find((c) => c.id === activeCam)?.label ?? "—";
  const activeCamTile = CAMS.find((c) => c.id === activeCam)?.tile ?? "cam1";
  const padSlots = useMemo(
    () => BUTTON_ORDER.map((button) => ({
      button,
      preset: presetForButton(scenario, activeCam, button) ?? null,
    })),
    [scenario, activeCam],
  );
  const lastPreset =
    activePreset !== null ? presetForButton(scenario, activeCam, activePreset) : null;
  // Zoom percentage is computed against the widest preset on this cam in
  // this scenario — that's the operational "1.0×" baseline. If the cam has
  // no presets yet (sparser scenarios), fall back to a normalized 1.0.
  const widestCropWidth = useMemo(() => {
    const list = scenario.presets[activeCam] ?? [];
    if (list.length === 0) return 1;
    return list.reduce((max, p) => Math.max(max, p.crop.width), 0);
  }, [scenario, activeCam]);
  const zoomPct = Math.round(Math.max(0, (widestCropWidth / activeCrop.width - 1) * 100));

  return (
    <HelpShell panelKind="ptz-aw-rp50">
      <style>{PANEL_STYLES}</style>
      <ScaledSurface designWidth={1800}>
      <div className="ptz-stage" role="group" aria-label="PTZ controller">

        <div className="ptz-mv">
        <MultiviewCO160a
          pgmSource={controlledPgm ?? "cam2"}
          pvwSource={controlledPvw ?? activeCamTile}
          size="wide"
          cameraFraming={cameraFraming}
          controllingSource={activeCamTile}
          ariaLabel={`Multiview — controlling ${activeCamLabel} with virtual framing indicator`}
        />
      </div>

      <div className="ptz-active-cam" role="status" aria-live="polite">
        CONTROLLING · <strong>{activeCamLabel}</strong> · joystick + zoom route here
        <span className="ptz-mode-pill">
          MODE: {cameraMode === "ic_manila" ? "IC MANILA" : "STANDARD ASSEMBLY"}
        </span>
      </div>

      <div className="ptz-panel">
        {/* LEFT — cam select + group + LCD + menu/WB */}
        <div className="ptz-sec">
          <div className="ptz-sec-label">CAMERA SELECT</div>
          <div className="ptz-cam-select" data-hotspot-id="cam-select">
            {CAMS.map((cam) => {
              const enabled = isCamEnabled(cam.id);
              return (
                <button
                  key={cam.id}
                  type="button"
                  className={`ptz-cam-btn ${activeCam === cam.id ? "active" : ""} ${enabled ? "" : "disabled"}`}
                  onClick={() => selectCam(cam.id)}
                  disabled={!enabled}
                  aria-label={`Select ${cam.label}`}
                  aria-pressed={activeCam === cam.id}
                >
                  {cam.label}
                </button>
              );
            })}
          </div>

          <div className="ptz-lcd" data-hotspot-id="lcd">
            <div className="row">
              <span>SEL</span>
              <span className="amber">{activeCamLabel}</span>
            </div>
            <div className="row">
              <span>LAST</span>
              <span className="amber">
                {lastPreset && activePreset !== null
                  ? `P-${String(activePreset).padStart(2, "0")} ${lastPreset.label}`
                  : "—"}
              </span>
            </div>
            <div className="row">
              <span>ZOOM</span>
              <span>{zoomPct}%</span>
            </div>
            <div className="row">
              <span>IRIS</span>
              <span>F4.0</span>
            </div>
            <div className="row">
              <span>TALLY</span>
              <span style={{ color: activeCam === "cam2" ? "#ff5566" : "#2ade67" }}>
                {activeCam === "cam2" ? "PGM" : "PVW"}
              </span>
            </div>
            <div className="row">
              <span>MODE</span>
              <span className="amber">{cameraMode === "ic_manila" ? "IC" : "ASMBL"}</span>
            </div>
          </div>

          <div className="ptz-sec-label">MENU · WHITE BALANCE</div>
          <div className="ptz-group-row" data-hotspot-id="menu-wb">
            <span className="ptz-group-btn">MENU</span>
            <span className="ptz-group-btn">OSD</span>
            <span className="ptz-group-btn">AWB</span>
            <span className="ptz-group-btn">ABB</span>
          </div>
        </div>

        {/* CENTER — joystick + dials */}
        <div className="ptz-sec">
          <div className="ptz-sec-label" style={{ textAlign: "center" }}>
            JOYSTICK · LIVE PAN / TILT / ZOOM
          </div>
          <div className="ptz-jstick-wrap">
            <div
              ref={joyRef}
              className={`ptz-jstick-base ${joystickActive ? "active" : ""}`}
              data-hotspot-id="joystick"
              onMouseDown={(e) => handleJoyStart(e.clientX, e.clientY)}
              onTouchStart={(e) => {
                const t = e.touches[0];
                if (t) handleJoyStart(t.clientX, t.clientY);
              }}
              role="application"
              aria-label="PTZ joystick — drag to pan and tilt the active camera"
            >
              <span
                className="ptz-jstick-stick"
                style={{
                  transform: `translate(${joystickPos.x - 24}px, ${joystickPos.y - 24}px)`,
                  transition: joystickActive ? "none" : "transform 0.15s ease",
                }}
              />
            </div>
            <div className="ptz-zoom-rocker" data-hotspot-id="zoom-rocker">
              <span className="ptz-zoom-label">WIDE</span>
              <button
                type="button"
                className="ptz-zoom-btn"
                onClick={() => {
                  updateActiveCrop((prev) => zoomCrop(prev, 1.1));
                  onAction?.({ kind: "zoom", cam: activeCam, delta: -0.1, ts_ms: Date.now() });
                }}
                aria-label="Zoom out"
              >
                −
              </button>
              <button
                type="button"
                className="ptz-zoom-btn"
                onClick={() => {
                  updateActiveCrop((prev) => zoomCrop(prev, 0.9));
                  onAction?.({ kind: "zoom", cam: activeCam, delta: +0.1, ts_ms: Date.now() });
                }}
                aria-label="Zoom in"
              >
                +
              </button>
              <span className="ptz-zoom-label">TELE</span>
            </div>
            <p className="ptz-help">DRAG · ARROW KEYS · +/− zoom</p>
          </div>

          <div className="ptz-dial-row" data-hotspot-id="dial-row">
            <DialWrap label="IRIS" value="F4.0" rotate={12} />
            <DialWrap label="FOCUS" value="AUTO" rotate={-22} />
            <DialWrap label="PED" value="0" rotate={0} />
          </div>
          <div className="ptz-group-row">
            <span className="ptz-group-btn">GAIN −</span>
            <span className="ptz-group-btn">GAIN +</span>
            <span className="ptz-group-btn">SHUTTER</span>
            <span className="ptz-group-btn">FILTER</span>
          </div>
        </div>

        {/* RIGHT — preset pad + user buttons + speed */}
        <div className="ptz-sec">
          <div className="ptz-sec-label">PRESET RECALL</div>
          <div className="ptz-preset-pad" data-hotspot-id="preset-pad">
            {padSlots.map(({ button, preset }) => {
              const empty = preset === null;
              const label = preset?.label ?? "—";
              return (
                <button
                  key={button}
                  type="button"
                  className={`ptz-preset-key ${activePreset === button ? "recalled" : ""} ${empty ? "empty" : ""}`}
                  onClick={() => !empty && recallPreset(button)}
                  disabled={empty}
                  aria-label={
                    empty
                      ? `Preset ${button}: empty for ${activeCamLabel} in this scenario`
                      : `Recall preset ${button}: ${label}`
                  }
                  aria-pressed={activePreset === button}
                >
                  <span className="ptz-preset-num">{button}</span>
                  <span className="ptz-preset-lbl">{label}</span>
                </button>
              );
            })}
            <span className="ptz-preset-key" aria-hidden>
              <span className="ptz-preset-num">·</span>
            </span>
            <span className="ptz-preset-key" aria-hidden>
              <span className="ptz-preset-num">SHFT</span>
              <span className="ptz-preset-lbl">+10</span>
            </span>
          </div>
          <div className="ptz-preset-controls" data-hotspot-id="preset-controls">
            <button
              type="button"
              className="ptz-pc-btn"
              onClick={() => activePreset !== null && storePreset(activePreset)}
              disabled={activePreset === null}
              aria-label="Store current framing into active preset"
            >
              STORE
            </button>
            <button
              type="button"
              className="ptz-pc-btn"
              onClick={() => activePreset !== null && recallPreset(activePreset)}
              aria-label="Recall preset"
            >
              RECALL
            </button>
          </div>

          <div className="ptz-sec-label" style={{ marginTop: 14 }}>
            USER BUTTONS
          </div>
          <div className="ptz-user-grid" data-hotspot-id="user-buttons">
            {Array.from({ length: 8 }).map((_, i) => (
              <span
                key={`u-${i}`}
                className={`ptz-user-btn ${i === 1 ? "active" : ""}`}
                aria-hidden
              >
                U {i + 1}
              </span>
            ))}
          </div>

          <div className="ptz-sec-label" style={{ marginTop: 10 }}>
            SPEED
          </div>
          <div className="ptz-speed-row" data-hotspot-id="speed-row">
            <span className="ptz-speed-label">PAN/TILT</span>
            <div className="ptz-speed-grid">
              {[1, 2, 3, 4].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`ptz-user-btn small ${speed === s ? "active" : ""}`}
                  onClick={() => setSpeed(s as 1 | 2 | 3 | 4)}
                  aria-label={`Speed ${s}`}
                  aria-pressed={speed === s}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <p className="ptz-shortcuts">
        Shortcuts: ↑ ↓ ← → pan/tilt · 0‑9 recall preset · +/− zoom
      </p>
    </div>
    </ScaledSurface>
    </HelpShell>
  );
}

function DialWrap({
  label,
  value,
  rotate,
}: {
  label: string;
  value: string;
  rotate: number;
}) {
  return (
    <div className="ptz-dial-wrap">
      <span className="ptz-dial-lbl">{label}</span>
      <span className="ptz-dial" style={{ transform: `rotate(${rotate}deg)` }} />
      <span className="ptz-dial-val">{value}</span>
    </div>
  );
}

const PANEL_STYLES = `
/* Stage authored at design width inside <ScaledSurface>; viewport
   media queries are not used here. */
.ptz-stage {
  background: linear-gradient(180deg, #08090d 0%, #040509 100%);
  padding: 18px;
  border-radius: 14px;
  color: #e7e8ec;
  font-family: -apple-system, "SF Pro Text", system-ui, sans-serif;
  box-shadow: 0 40px 80px -30px rgba(0,0,0,0.9);
  display: grid;
  /* Panel sits at its intrinsic width (real AW-RP50 proportions); multiview
     takes the remaining horizontal room so it can render at its widest size
     instead of being squeezed by a flex-grow panel column. */
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-areas:
    "banner banner"
    "panel  mv";
  column-gap: 18px;
  row-gap: 12px;
  align-items: start;
}
.ptz-mv { grid-area: mv; align-self: start; }

.ptz-active-cam {
  grid-area: banner;
  text-align: center;
  font-size: 12px; letter-spacing: 2px; font-weight: 700;
  padding: 10px;
  background: linear-gradient(90deg, transparent 0%, rgba(255,191,71,0.15) 50%, transparent 100%);
  border-bottom: 1px solid rgba(255,191,71,0.3);
  color: #ffbf47;
  display: flex; gap: 12px; align-items: center; justify-content: center;
  flex-wrap: wrap;
}
.ptz-active-cam strong { color: #ffd170; letter-spacing: 1.5px; }
.ptz-mode-pill {
  margin-left: 8px;
  padding: 3px 10px;
  border: 1px solid rgba(255,191,71,0.4);
  border-radius: 12px;
  font-size: 10px; letter-spacing: 1px;
  color: #ffd170;
  background: rgba(255,191,71,0.06);
}

.ptz-panel {
  grid-area: panel;
  background: linear-gradient(180deg, #2a2c32 0%, #1a1c20 50%, #14161a 100%);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #3a3d46;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.5), 0 8px 20px -8px rgba(0,0,0,0.6);
  display: grid;
  /* Real AW-RP50 column proportions: left LCD/cam-select stack ≈ 31%,
     center joystick assembly is the smallest at ≈ 27% (the joystick disk
     is small and tightly framed in hardware), right preset pad is the
     widest at ≈ 35% to fit the 3-wide preset grid. Center used to be 1fr
     and ballooned to ~760px on a 1800px design surface; that's why the
     joystick floated in empty space. */
  grid-template-columns: 240px 220px 280px;
  gap: 14px;
  min-width: 0;
}

.ptz-sec {
  background: linear-gradient(180deg, #15171c 0%, #0f1116 100%);
  border-radius: 5px;
  padding: 12px;
  border: 1px solid rgba(255,255,255,0.04);
  position: relative;
  min-width: 0;
}
.ptz-sec-label {
  font-size: 7px; letter-spacing: 2px;
  color: #ffbf47; font-weight: 700;
  margin-bottom: 10px; opacity: 0.85;
}

.ptz-cam-select {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; margin-bottom: 10px;
}
.ptz-cam-btn {
  aspect-ratio: 1.4/1;
  border-radius: 3px;
  background: linear-gradient(180deg, #3a3d46 0%, #24272e 100%);
  border: 1px solid #4a4d56;
  font-size: 11px; font-weight: 800; letter-spacing: 1px;
  color: #c8cad0;
  display: flex; align-items: center; justify-content: center;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.07), 0 1px 2px rgba(0,0,0,0.4);
  cursor: pointer;
  text-align: center; padding: 4px;
}
.ptz-cam-btn:focus-visible { outline: 2px solid #ffbf47; outline-offset: 1px; }
.ptz-cam-btn.active {
  background: linear-gradient(180deg, #ffbf47 0%, #d49a20 100%);
  color: #0b0c10; border-color: #ffd170;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.25), 0 0 12px -2px rgba(255,191,71,0.6);
}
.ptz-cam-btn.disabled, .ptz-cam-btn:disabled {
  opacity: 0.4; cursor: not-allowed;
  background: linear-gradient(180deg, #2a2d36 0%, #15171c 100%);
}

.ptz-lcd {
  background: #0a2818; color: #3ae877;
  font-family: "SF Mono", ui-monospace, monospace;
  font-size: 9px; padding: 8px 9px;
  border-radius: 2px; border: 1px solid #1a4a2a;
  line-height: 1.4; box-shadow: inset 0 1px 3px rgba(0,0,0,0.7);
  margin: 10px 0;
}
.ptz-lcd .row { display: flex; justify-content: space-between; }
.ptz-lcd .amber { color: #ffbf47; }

.ptz-group-row { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.ptz-group-btn {
  font-size: 9px; font-weight: 700; letter-spacing: 1px;
  padding: 6px 0; border-radius: 3px; text-align: center;
  background: linear-gradient(180deg, #3a3d46 0%, #24272e 100%);
  border: 1px solid #4a4d56; color: #c8cad0;
  display: block;
}

.ptz-jstick-wrap {
  display: flex; flex-direction: column; align-items: center;
  padding: 14px 0 10px; gap: 10px;
}
.ptz-jstick-base {
  width: 180px; height: 180px;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 42%, #2a2d36 0%, #15171c 70%, #0a0b0e 100%);
  border: 2px solid #3a3d46;
  position: relative;
  box-shadow: inset 0 4px 12px rgba(0,0,0,0.8), 0 2px 6px rgba(0,0,0,0.5);
  touch-action: none;
  user-select: none;
  cursor: grab;
}
.ptz-jstick-base.active { cursor: grabbing; }
.ptz-jstick-base::before {
  content: ""; position: absolute; inset: 10px;
  background:
    linear-gradient(to right, transparent 49%, rgba(255,255,255,0.06) 49%, rgba(255,255,255,0.06) 51%, transparent 51%),
    linear-gradient(to bottom, transparent 49%, rgba(255,255,255,0.06) 49%, rgba(255,255,255,0.06) 51%, transparent 51%);
  border-radius: 50%;
}
.ptz-jstick-stick {
  position: absolute;
  top: 50%; left: 50%;
  width: 48px; height: 48px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #aaa 0%, #555 40%, #1a1a1a 100%);
  border: 1.5px solid #555;
  box-shadow: 0 6px 14px rgba(0,0,0,0.8), inset 0 2px 4px rgba(255,255,255,0.15);
  display: block;
}
.ptz-jstick-stick::after {
  content: ""; position: absolute; inset: 10px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #444 0%, #1a1a1a 100%);
  border: 1px solid #2a2a2a;
}

.ptz-zoom-rocker {
  display: flex; align-items: center; gap: 8px;
  background: linear-gradient(180deg, #1a1c22 0%, #0a0b0e 100%);
  padding: 6px 14px; border-radius: 20px;
  border: 1px solid #2a2d36;
}
.ptz-zoom-btn {
  padding: 4px 12px; border-radius: 3px;
  background: linear-gradient(180deg, #3a3d46 0%, #24272e 100%);
  border: 1px solid #4a4d56;
  font-size: 12px; font-weight: 700; letter-spacing: 1px;
  color: #c8cad0;
  cursor: pointer;
}
.ptz-zoom-btn:focus-visible { outline: 2px solid #ffbf47; outline-offset: 1px; }
.ptz-zoom-label { font-size: 8px; color: #666973; font-weight: 700; letter-spacing: 1.5px; }

.ptz-help { margin: 0; font-size: 7px; color: #666973; letter-spacing: 1.2px; font-weight: 700; }

.ptz-dial-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 12px 0 8px; }
.ptz-dial-wrap { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.ptz-dial {
  width: 42px; height: 42px; border-radius: 50%;
  background: radial-gradient(circle at 30% 25%, #5a5d66 0%, #2a2d36 60%, #15171c 100%);
  border: 1px solid #4a4d56;
  position: relative;
  box-shadow: inset 0 1px 1px rgba(255,255,255,0.1), 0 1px 3px rgba(0,0,0,0.6);
  display: block;
}
.ptz-dial::after {
  content: ""; position: absolute; top: 4px; left: 50%;
  transform: translateX(-50%); width: 2.5px; height: 10px;
  background: #ffbf47; border-radius: 1px;
}
.ptz-dial-lbl { font-size: 7px; letter-spacing: 1px; color: #9ba0ab; font-weight: 700; }
.ptz-dial-val { font-size: 8px; color: #3ae877; font-family: "SF Mono", monospace; font-variant-numeric: tabular-nums; }

.ptz-preset-pad { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; }
.ptz-preset-key {
  aspect-ratio: 1/1;
  border-radius: 3px;
  background: linear-gradient(180deg, #3a3d46 0%, #24272e 100%);
  border: 1px solid #4a4d56;
  font-weight: 800;
  color: #c8cad0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.07), 0 1px 2px rgba(0,0,0,0.4);
  cursor: pointer;
  padding: 4px;
}
.ptz-preset-key:focus-visible { outline: 2px solid #ffbf47; outline-offset: 1px; }
.ptz-preset-num { font-size: 13px; line-height: 1; }
.ptz-preset-lbl {
  font-size: 6px; color: #ffbf47; letter-spacing: 0.4px;
  font-weight: 700; margin-top: 2px;
}
.ptz-preset-key.recalled {
  background: linear-gradient(180deg, #ffbf47 0%, #d49a20 100%);
  color: #0b0c10; border-color: #ffd170;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.25), 0 0 10px -2px rgba(255,191,71,0.6);
}
.ptz-preset-key.recalled .ptz-preset-lbl { color: rgba(0,0,0,0.65); }
.ptz-preset-key.empty, .ptz-preset-key:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  background: linear-gradient(180deg, #2a2d36 0%, #15171c 100%);
}
.ptz-preset-key.empty .ptz-preset-lbl { color: #666973; }

.ptz-preset-controls { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; margin-top: 6px; }
.ptz-pc-btn {
  font-size: 9px; font-weight: 700; letter-spacing: 1px;
  padding: 6px 0; border-radius: 3px; text-align: center;
  background: linear-gradient(180deg, #3a3d46 0%, #24272e 100%);
  border: 1px solid #4a4d56; color: #c8cad0;
  cursor: pointer;
}
.ptz-pc-btn:focus-visible { outline: 2px solid #ffbf47; outline-offset: 1px; }

.ptz-user-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; }
.ptz-user-btn {
  font-size: 7px; font-weight: 700; letter-spacing: 0.5px;
  padding: 5px 0; border-radius: 2px; text-align: center;
  background: linear-gradient(180deg, #3a3d46 0%, #24272e 100%);
  border: 1px solid #4a4d56; color: #c8cad0;
  display: block;
  cursor: default;
}
.ptz-user-btn.small { font-size: 8px; padding: 3px 6px; cursor: pointer; }
.ptz-user-btn.small:focus-visible { outline: 2px solid #ffbf47; outline-offset: 1px; }
.ptz-user-btn.active {
  background: linear-gradient(180deg, #2ade67 0%, #189c4a 100%);
  color: #0b0c10; border-color: #3ae877;
}

.ptz-speed-row {
  background: #0a0b0e; padding: 6px 8px;
  border-radius: 2px; border: 1px solid #1a1c22;
  display: flex; justify-content: space-between; align-items: center;
}
.ptz-speed-label { font-size: 7px; color: #666973; letter-spacing: 1.2px; font-weight: 700; }
.ptz-speed-grid { display: flex; gap: 3px; }

.ptz-shortcuts { margin-top: 12px; text-align: center; font-size: 10px; color: #666973; letter-spacing: 0.4px; }
`;
